import {
  type ClientDetail,
  type ClientField,
  type ClientFieldInput,
  type ClientFieldPatch,
  type ClientRecord,
  type ClientRecordPatch,
  type CreateClient,
  type CreateSavedView,
  type SavedView,
  type SavedViewList,
} from '@cyberscore/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useSession } from '../../lib/session';
import { refreshLists } from '../../lib/list';

/** Client records, custom client fields and saved views (task 66). */

const clientKey = (t: string | undefined, key: string) => ['clients', t, key] as const;
const fieldsKey = (t: string | undefined) => ['client-fields', t] as const;
const viewsKey = (t: string | undefined, list: SavedViewList) => ['views', t, list] as const;
const path = (key: string) => `/clients/${encodeURIComponent(key)}`;

export function useClient(key: string | null) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: clientKey(tenant?.id, key ?? ''),
    queryFn: () => api<ClientDetail>('GET', path(key!)),
    enabled: !!tenant && !!key,
  });
}

export function useCreateClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateClient) => api<ClientRecord>('POST', '/clients', body),
    onSuccess: () => refreshLists(qc),
  });
}

export function useUpdateClient(key: string) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return useMutation({
    mutationFn: (body: ClientRecordPatch) => api<ClientRecord>('PATCH', path(key), body),
    onSuccess: (record) => {
      qc.setQueryData<ClientDetail>(clientKey(tenant?.id, key), (d) => (d ? { ...d, record } : d));
      void refreshLists(qc);
    },
  });
}

export function useClientFields() {
  const { tenant } = useSession();
  return useQuery({
    queryKey: fieldsKey(tenant?.id),
    queryFn: () => api<ClientField[]>('GET', '/settings/client-fields'),
    enabled: !!tenant,
  });
}

export function useClientFieldChanges() {
  const qc = useQueryClient();
  const { tenant } = useSession();
  const done = (list: ClientField[]) => {
    qc.setQueryData(fieldsKey(tenant?.id), list);
    void qc.invalidateQueries({ queryKey: ['clients', tenant?.id] });
  };
  return {
    create: useMutation({
      mutationFn: (body: ClientFieldInput) => api<ClientField[]>('POST', '/settings/client-fields', body),
      onSuccess: done,
    }),
    update: useMutation({
      mutationFn: ({ key, ...body }: ClientFieldPatch & { key: string }) =>
        api<ClientField[]>('PATCH', `/settings/client-fields/${key}`, body),
      onSuccess: done,
    }),
    remove: useMutation({
      mutationFn: (key: string) => api<ClientField[]>('DELETE', `/settings/client-fields/${key}`),
      onSuccess: done,
    }),
    order: useMutation({
      mutationFn: (keys: string[]) => api<ClientField[]>('PUT', '/settings/client-fields/order', { keys }),
      onSuccess: done,
    }),
  };
}

export function useSavedViews(list: SavedViewList) {
  const { tenant } = useSession();
  return useQuery({
    queryKey: viewsKey(tenant?.id, list),
    queryFn: () => api<SavedView[]>('GET', `/views?list=${list}`),
    enabled: !!tenant,
  });
}

export function useSavedViewChanges(list: SavedViewList) {
  const qc = useQueryClient();
  const { tenant } = useSession();
  return {
    create: useMutation({
      mutationFn: (body: Omit<CreateSavedView, 'list'>) =>
        api<SavedView[]>('POST', '/views', { ...body, list }),
      onSuccess: (views) => qc.setQueryData(viewsKey(tenant?.id, list), views),
    }),
    remove: useMutation({
      mutationFn: (id: string) => api('DELETE', `/views/${id}`),
      onSuccess: () => qc.invalidateQueries({ queryKey: viewsKey(tenant?.id, list) }),
    }),
  };
}
